import {ReactComponent as IconWallet} from '#/components/icons/ico-wallet.svg';
import {Web3AuthAdapterIcon} from '#/components/icons/web3auth-connected-adapter-icon';
import {WalletContext} from '#/contexts/wallet-provider';
import {tokenConstants} from '#/data/token-constants';
import environment from '#/environment/environment';
import {useNativeTokenBalance} from '#/hooks/query/use-native-token-balance';
import {useStableTokenBalance} from '#/hooks/query/use-stable-token-balance';
import {PortfolioTab} from '#/navigation/tabs';
import {ExchangeRoute} from '#/routes/exchange/exchange-route';
import {getExchangeRouteWithQueryParams} from '#/routes/exchange/get-exchange-route-with-query-params';
import {DefaultChildren} from '#/utils/utils';
import {FunctionComponent, ReactNode, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

export const WalletInfo: FunctionComponent<DefaultChildren> = () => {
  const {
    accountResult: {chain, isConnected},
    web3Auth,
  } = useContext(WalletContext);

  const {t} = useTranslation('translation');

  const {data: stableTokenBalance} = useStableTokenBalance();
  const {data: ethTokenBalance} = useNativeTokenBalance();

  if (isConnected) {
    return (
      <div className='gap-3 h-full flex items-center whitespace-nowrap'>
        {!environment.isWaitlistNet && (
          <div className='hidden md:flex h-full flex-col items-end text-xs text-white '>
            <TokenBalanceIndicator tokenName={tokenConstants.USDC}>
              {stableTokenBalance !== -1n &&
                t('utils.stableAmount', {
                  amount: Number(stableTokenBalance),
                })}
            </TokenBalanceIndicator>

            <TokenBalanceIndicator tokenName={chain?.nativeCurrency.symbol ?? ''}>
              {ethTokenBalance !== -1n &&
                t('utils.nativeAmount', {
                  amount: Number(ethTokenBalance / 10n ** 12n),
                })}
            </TokenBalanceIndicator>
          </div>
        )}

        <Link
          to={getExchangeRouteWithQueryParams(ExchangeRoute.PORTFOLIO, {portfolioTabId: PortfolioTab.ASSETS})}
          className='gap-3 h-full flex items-center hover:opacity-70'
        >
          <IconSeparatorContentBox>
            {{
              icon: <IconWallet className='w-4 h-4 stroke-gray-400 fill-transparent' />,
              content: (
                <>
                  <Web3AuthAdapterIcon adapterName={web3Auth?.connectedAdapterName ?? web3Auth?.cachedAdapter} />
                  <span className='text-gray-300'>{t('components.header.walletInfo.connected')}</span>
                </>
              ),
            }}
          </IconSeparatorContentBox>
        </Link>
      </div>
    );
  }

  return (
    <div className='h-full hover:opacity-70'>
      <IconSeparatorContentBox>
        {{
          icon: <IconWallet className='w-4 h-4 stroke-gray-400 fill-transparent' />,
          content: <span className='text-gray-300'>{t('components.header.walletInfo.connecting')}</span>,
        }}
      </IconSeparatorContentBox>
    </div>
  );
};

const TokenBalanceIndicator: FunctionComponent<{
  children?: ReactNode | ReactNode[];
  className?: string;
  tokenName: string;
}> = ({children, className = '', tokenName}) => {
  return (
    <div className={`h-full flex items-center opacity-70 text-sm leading-tight ${className}`}>
      {!!children ? <span>{children}</span> : <span className='w-20 h-4/6 rounded bg-gray-500 animate-pulse' />}
      &nbsp;
      {tokenName}
    </div>
  );
};

const IconSeparatorContentBox: FunctionComponent<{
  children: {
    icon: ReactNode;
    content: ReactNode;
  };
  className?: string;
}> = ({children, className = ''}) => {
  return (
    <div
      className={`flex gap-2 p-2 justify-between items-center h-full rounded border border-gray-500 text-white group relative box-border ${className}`}
    >
      {children.icon}
      <span className='hidden sm:block w-[1px] h-full bg-gray-500' />
      <span className='hidden sm:flex-center gap-2'>{children.content}</span>
    </div>
  );
};
